import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { PiezaId, PlanDePieza } from '../../../lib/onboardingMarca/piezas';
import { dialogoDelRevelado } from '../../../lib/onboardingMarca/dialogoDePiezas';
import { registrarDescarga } from '../../../lib/onboardingMarca/descargaDelOnboarding';
import { descargarArchivo } from '../../../utils/descargarArchivo';
import { buildDownloadFileName } from '../../../utils/downloadFileName';
import { FondoCordillera } from './FondoCordillera';
import { Vitrina } from './Vitrina';
import './marcoDesktop.css';
import { GuiaCumbre, useDialogo, type Gesto } from './GuiaCumbre';
import './primerasPiezas.css';

export type Reaccion = 'me_gusta' | 'no_va';

export interface PiezaParaRevelar {
  plan: PlanDePieza;
  /** Sin url, la pieza todavía se está creando. */
  url?: string;
  fallo?: boolean;
}

function pideMenosMovimiento(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * El revelado de las piezas, una por una (ADR-2026-09-28-onboarding-v2-tablero-de-marca), con
 * la interfaz del onboarding (ADR-2026-10-02-onboarding-v2-piezas-con-cumbre-y-tienda): la
 * cordillera, los tramos de avance y Cumbre presentando cada pieza —cuál es, cómo se llama y
 * por qué—. Con "marca de agua", el logo se superpone como va a salir al descargar.
 *
 * Pantalla completa, en el teléfono y en desktop, como el resto del onboarding.
 */
export function RevelarPiezas(p: {
  piezas: PiezaParaRevelar[];
  logoAgua: string | null;
  /** Todavía hay piezas en camino. */
  creando: boolean;
  onTerminar: (reacciones: Partial<Record<PiezaId, Reaccion>>) => void;
}) {
  const [quieto] = useState(pideMenosMovimiento);
  const [i, setI] = useState(0);
  const [gesto, setGesto] = useState<Gesto | null>(null);
  const actual = p.piezas[i];
  const ultima = i === p.piezas.length - 1;
  // En la última no se sale mientras las demás siguen en camino: se irían sin verlas.
  const puedeTerminar = !ultima || !p.creando;

  // Sin "Me gusta / No va": sólo avanzar (pedido de Maxi, 2026-09-29). `onTerminar` sigue
  // recibiendo las reacciones —vacías— para no cambiar el contrato con la pantalla.
  const siguiente = useCallback(() => {
    if (ultima) {
      if (puedeTerminar) p.onTerminar({});
    } else setI((n) => n + 1);
  }, [ultima, puedeTerminar, p]);
  const anterior = () => setI((n) => Math.max(0, n - 1));

  // Descargar la pieza: el mismo mecanismo que el resto de la app (blob local, ver
  // ADR-2026-08-03-descarga-del-onboarding-usa-el-mecanismo-de-la-app). Si sale bien, al salir
  // del onboarding se muestran los planes (ADR-2026-10-05-onboarding-v2-descarga-abre-planes).
  const [descargando, setDescargando] = useState(false);
  const [falloLaDescarga, setFalloLaDescarga] = useState(false);
  const descargar = async () => {
    const pieza = p.piezas[i];
    if (!pieza?.url || descargando) return;
    setDescargando(true);
    setFalloLaDescarga(false);
    try {
      const ok = await descargarArchivo({
        url: pieza.url,
        fileName: buildDownloadFileName({ variantName: pieza.plan.etiqueta }),
      });
      if (ok) registrarDescarga();
    } catch {
      setFalloLaDescarga(true);
    } finally {
      setDescargando(false);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') siguiente();
      if (e.key === 'ArrowLeft') setI((n) => Math.max(0, n - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [siguiente]);

  const estado = actual?.url ? 'lista' : actual?.fallo ? 'fallo' : 'creando';
  // Depende de los DATOS de la pieza y no del objeto: la pantalla arma `piezas` de nuevo en
  // cada render, y con el objeto como dependencia el diálogo se reiniciaba antes de escribir
  // una letra (el globo quedaba vacío).
  const etiqueta = actual?.plan.etiqueta ?? '';
  const porques = actual?.plan.porques.join('\u0000') ?? '';
  const hayActual = Boolean(actual);
  const tramos = useMemo(
    () =>
      hayActual
        ? dialogoDelRevelado({
            n: i,
            total: p.piezas.length,
            etiqueta,
            porques: porques ? porques.split('\u0000') : [],
            estado,
          })
        : [],
    [hayActual, i, p.piezas.length, etiqueta, porques, estado],
  );
  // La primera espera a que la pantalla entre; las siguientes, casi nada.
  const dialogo = useDialogo(tramos, { instantaneo: quieto, arrancarEn: i === 0 ? 700 : 200 });

  // Cada pieza que aparece lista, Cumbre la festeja; la que no salió, la duda.
  useEffect(() => {
    if (estado === 'creando') return;
    setGesto((g) => ({ tipo: estado === 'lista' ? 'festeja' : 'duda', n: (g?.n ?? 0) + 1 }));
  }, [i, estado]);

  if (!actual) return null;

  // Portal al body: la pantalla entra con un fundido (opacity animada), que arma su
  // propio contexto de apilamiento, y adentro de él el z-50 no le gana a la barra.
  return createPortal(
    <div className="om-pantalla om-revelado" role="dialog" aria-modal="true" aria-label="Tus primeras piezas">
      {/* En desktop, como el resto del onboarding: recuadro al centro sobre el fondo
          oscurecido y la vitrina a los costados (ADR-2026-10-02-onboarding-v2-revelado-en-recuadro).
          En el teléfono el recuadro es `display: contents` y lo demás no se ve. */}
      <div className="om-fondo-desktop" aria-hidden="true" />
      <Vitrina />
      <div className="om-revelado__tarjeta">
      <FondoCordillera quieto={quieto} />

      <div className="om-revelado__dentro">
        <ol className="om-revelado__tramos" aria-hidden="true">
          {p.piezas.map((pz, n) => (
            <li key={pz.plan.id} className={`om-paso ${n < i ? 'is-hecho' : n === i ? 'is-actual' : ''}`} />
          ))}
        </ol>

        <div className="om-revelado__cuerpo">
          <div className="om-revelado__guia">
            <GuiaCumbre
              tramos={tramos}
              letras={dialogo.letras}
              hablando={dialogo.hablando}
              terminado={dialogo.terminado}
              onCompletar={dialogo.completar}
              reaccion={null}
              animo={estado === 'creando' ? 'piensa' : 'escucha'}
              gesto={gesto}
              instantaneo={quieto}
            />
          </div>

          <div
            key={`${actual.plan.id}-${estado}`}
            className={`om-revelado__marco ${estado === 'lista' ? 'om-revela' : 'om-llega'}`}
          >
            {actual.url ? (
              <>
                <img src={actual.url} alt={actual.plan.etiqueta} className="h-full w-full object-cover" />
                {p.logoAgua && (
                  <img src={p.logoAgua} alt="" aria-hidden="true" className="absolute bottom-[4%] right-[4%] w-[18%] opacity-60" />
                )}
              </>
            ) : (
              <div className={`om-revelado__vacio ${actual.fallo ? '' : 'om-esqueleto'}`} aria-live="polite">
                <span className="material-symbols-rounded" aria-hidden="true">
                  {actual.fallo ? 'image_not_supported' : 'auto_awesome'}
                </span>
                {actual.fallo ? 'Esta no salió' : 'Creando tu pieza…'}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="om-barra">
        <div className="om-barra__dentro">
          <button
            type="button"
            onClick={siguiente}
            disabled={!puedeTerminar}
            className="om-boton md:order-last md:w-auto md:px-10"
          >
            {!ultima ? 'Siguiente' : puedeTerminar ? 'Seguir' : 'Terminando las demás…'}
          </button>
          {actual.url && (
            <button
              type="button"
              onClick={() => void descargar()}
              disabled={descargando}
              className="om-revelado__descargar md:order-1"
            >
              <span className="material-symbols-rounded" aria-hidden="true">
                download
              </span>
              {descargando ? 'Descargando…' : 'Descargar'}
            </button>
          )}
          {falloLaDescarga && (
            <p role="alert" className="om-barra__aviso text-center text-[#991B1B]">
              No pudimos descargarla. Intenta de nuevo.
            </p>
          )}
          {i > 0 && (
            <button type="button" onClick={anterior} className="om-canal__despues self-center md:order-first">
              Anterior
            </button>
          )}
        </div>
      </div>
      </div>
    </div>,
    document.body,
  );
}
